import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Clock, History, ImagePlus, Loader2, Plus, Sparkles } from "lucide-react";

import type { EditorHandoff } from "@/shared/lib/advisorHandoff";
import { classifyQuote, priceText, splitQuoteSummary } from "@/shared/lib/advisorQuote";
import { Card, CardContent } from "@/shared/ui/card";
import { HandoffRow } from "./HandoffRow";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Input } from "@/shared/ui/input";
import { IMAGE_ACCEPT } from "@/shared/lib/uploadImageFormats";
import type { Scene } from "@/shared/lib/advisorScene";

import { advisorDiagnosticLine } from "../model/advisorError";
import { stripConfirmEcho } from "../model/chatState";
import { useAdvisorChat, useProactiveOpen } from "../model/useAdvisorChat";
import { useImagePicker } from "../model/advisorUploads";
import { advisorTopicsApi } from "../api/advisorTopicsApi";
import { advisorChatApi } from "../api/advisorChatApi";
import {
  contextNotice,
  switchedToRequested,
  switchFailedNotice,
  type ContextNotice,
} from "../model/contextState";
import { useAdvisorBriefing } from "../model/useAdvisorBriefing";
import { AdvisorBriefing } from "./AdvisorBriefing";
import { RecentCreationsStrip } from "./RecentCreationsStrip";
import { SceneSkillBar } from "./SceneSkillBar";
import { SkillGrid } from "./SkillGrid";
import { SkillChips } from "./SkillChips";
import { MarkdownText } from "./MarkdownText";
import { AiGeneratedNote } from "@/entities/compliance";
import { ThinkingIndicator } from "./ThinkingIndicator";
import { TopicHistory } from "./TopicHistory";
import type { EvidenceItem, StyleGalleryItem } from "../model/chatTypes";
import { usePersona } from "@/shared/brand/persona";

// 样例图/渐变占位与 create 页画廊同源约定(key → public/style-samples/{key}.webp)。
// FSD:entities 不 import @/features,两个小 helper 本地化(算法与 styleOptions.ts 一致)。
function styleSampleSrc(key: string): string {
  return `/style-samples/${key}.webp`;
}
function stylePlaceholder(key: string): string {
  const pairs = [
    ["#6366f1", "#a855f7"],
    ["#0ea5e9", "#22d3ee"],
    ["#f59e0b", "#ef4444"],
    ["#10b981", "#14b8a6"],
    ["#ec4899", "#8b5cf6"],
    ["#64748b", "#94a3b8"],
    ["#f43f5e", "#fb923c"],
    ["#3b82f6", "#6366f1"],
  ];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  const [a, b] = pairs[h % pairs.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
}

/** 风格卡缩略:样例图加载失败回落确定性渐变。 */
function StyleCardThumb({ item }: { item: StyleGalleryItem }) {
  const [failed, setFailed] = useState(false);
  return (
    <div
      className="relative aspect-[4/3] w-full overflow-hidden rounded-md"
      style={{ background: stylePlaceholder(item.key) }}
    >
      {!failed && (
        <img
          src={styleSampleSrc(item.sampleKey ?? item.key)}
          alt={item.label}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

/** 横滑风格样例卡。onPick 收整条目:消息流路径用 label 说进对话,确认门路径用 key 走 pick-style。 */
function StyleGalleryRow({
  styles,
  onPick,
}: {
  styles: StyleGalleryItem[];
  onPick: (item: StyleGalleryItem) => void;
}) {
  return (
    <div className="flex gap-2 overflow-x-auto overscroll-x-contain pb-1">
      {styles.map((s) => (
        <button
          key={s.key}
          type="button"
          aria-label={`用「${s.label}」风格出图`}
          onClick={() => onPick(s)}
          className="w-32 shrink-0 space-y-1 rounded-lg border bg-card p-1.5 text-left transition-colors hover:border-primary/50"
        >
          <StyleCardThumb item={s} />
          <div className="text-xs font-semibold leading-tight">{s.label}</div>
          {s.blurb && (
            <div className="line-clamp-2 text-2xs leading-snug text-muted-foreground">
              {s.blurb}
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

function Bubble({
  who,
  text,
  styleGallery,
  handoffs,
  onPickStyle,
  aiLabel = false,
}: {
  aiLabel?: boolean;
  who: "user" | "advisor";
  text: string;
  evidence?: EvidenceItem[];
  styleGallery?: StyleGalleryItem[];
  handoffs?: EditorHandoff[];
  onPickStyle?: (item: StyleGalleryItem) => void;
}) {
  const mine = who === "user";
  return (
    // ADV-UI-READ-1(2026-08-26 老板「看着不和谐 / 文字显示不对称」):
    // ★病灶是**两侧用同一套几何、两套视觉**。真机量过:两边都是 max-w-[80%],
    //   在 1600px 宽屏上 = 830px、每行 55 个汉字;而用户消息是实心品牌紫的气泡、
    //   军师回复是 `bg-muted`(商务皮肤下几乎等于页底),读起来一个是卡片、
    //   一个是贴着左边缘的一整块灰底文档 —— 它们不像同一个线程里的东西。
    // ★修法不是调色:军师回复**不做气泡**(去底色,改一条品牌色左规线),
    //   用户消息保留气泡。一边是「说的话」一边是「给的答复」,靠形态区分。
    // ★两侧同时收到 38rem = 608px ≈ 每行 34 个汉字(中文可读宽度)。
    <div className={`my-3 flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[38rem] space-y-1.5">
        {(text || (!styleGallery && !handoffs)) &&
          (mine ? (
            <div className="whitespace-pre-wrap rounded-2xl rounded-br-md bg-primary px-3.5 py-2.5 text-base leading-relaxed text-primary-foreground">
              {text}
            </div>
          ) : (
            <div className="border-l-2 border-primary/50 pl-4 text-base leading-[1.7]">
              <MarkdownText text={text} />
              {/* 1011 算法备案 §5:AI 文本回复下方可见标识(租户 compliance.visibleLabel 控制) */}
              {aiLabel && text && <AiGeneratedNote />}
            </div>
          ))}
        {!mine && styleGallery && onPickStyle && (
          <StyleGalleryRow styles={styleGallery} onPick={onPickStyle} />
        )}
        {/* ADV-HANDOFF-PC-1:交接卡 —— 此前这条 action 在 PC 侧被静默丢弃 */}
        {!mine && handoffs && handoffs.length > 0 && <HandoffRow handoffs={handoffs} />}
        {/* ★2026-08-28 撤「已参考 N 项资料 → 军师查了:read_geo_score」证据行:对客暴露英文工具名影响观感(同 H5)。 */}
      </div>
    </div>
  );
}

/**
 * @param scene 唤起军师时用户所在页面的场景快照(见 shared/lib/advisorScene);技能网格据此置顶。
 *   由上层在 open: false→true 那一刻快照并冻结,面板存活期间不随路由变化 —— 否则用户切页时
 *   网格会在眼皮底下重排。
 */
/**
 * 「历史看得见 / 模型不记得」的那句话。判据钉服务端 contextState,不钉时间不钉文案。
 * ★抽出来是因为它**必须在用户当下所在的那一屏**出现:切历史失败时人还停在历史视图,
 *   而这条横幅原来只画在聊天视图里 —— 等于"最该说话的那一刻恰好是哑的"。
 */
function ContextNoticeBar({ notice }: { notice: ContextNotice | null }) {
  if (!notice) return null;
  return (
    <div
      role="status"
      data-testid="advisor-context-notice"
      className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 text-xs text-amber-700 dark:text-amber-300"
    >
      {notice.text}
    </div>
  );
}

export function AdvisorChatPanel({
  sessionId,
  scene,
  opening = "proactive",
  hideRecentShortcut = false,
  initialDraft,
  showAiLabel = true,
}: {
  /**
   * 1011 算法备案:军师回复下方「内容由AI生成」小字。调用方传 `useCompliance().visibleLabel`
   * (entities 不读 AuthContext);缺省 true = 合规兜底默认开。
   */
  showAiLabel?: boolean;
  sessionId: string;
  scene?: Scene | null;
  /**
   * ADV-UI-2 开场模式:proactive=浮层现状(LLM 开场白);briefing=满屏页三块开屏
   * (真招呼+任务卡+建议 chips,骨架屏全程不见白)。默认 proactive,浮层零回归。
   */
  opening?: "proactive" | "briefing";
  /**
   * 调用方已经把「最近作品」常驻在别处(满屏页右栏)→ 底部那颗快捷按钮不再重复。
   *
   * ★为什么是**显式 prop** 而不是在面板里写 `lg:hidden` 猜:
   *   本面板同时被**满屏页**(有右栏,但右栏自己是 `hidden lg:flex`)和**悬浮浮层**
   *   (任何宽度都没有右栏)复用。按断点猜的话,浮层在宽屏上会把唯一入口藏掉。
   *   ⇒ 「右栏在不在」只有调用方知道,不能由面板推断。
   * ★满屏页传的是 `hasRecentRail`,它自己再按断点决定要不要真的隐藏 ——
   *   窄屏右栏不显示时,底部这颗必须回来,否则最近作品在窄屏上彻底没有入口。
   */
  hideRecentShortcut?: boolean;
  /**
   * 1004 帮助中心行业篇「让{persona}来做」:进页时输入框预填这句话,**不自动发送**
   * (人看一眼、改两个字再按发送 —— 帮助页里的话是范例,不是替他下的指令)。只在挂载时读一次。
   */
  initialDraft?: string;
}) {
  const persona = usePersona();
  const qc = useQueryClient();
  const briefingMode = opening === "briefing";
  const proactive = useProactiveOpen(sessionId, !briefingMode);
  const briefing = useAdvisorBriefing(briefingMode);
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    messages,
    pendingConfirm,
    loading,
    error,
    send,
    newTopic,
    pickStyle,
    confirm,
    cancel,
    pendingUploads,
    attachUpload,
    removeUpload,
    hydrateMessages,
    replaceMessages,
  } = useAdvisorChat(sessionId);
  // S-photo(0819):PC 传图入口。逻辑与 H5 共用 advisorUploads,两端只各写各的 UI。
  const { uploading, uploadError, fileInputRef, pickImages, openPicker, clearUploadError } =
    useImagePicker(attachUpload);
  const [draft, setDraft] = useState(() => initialDraft ?? "");
  // 预填了就把光标放进输入框,回车即发;不预填不抢焦点(浮层开场不打断阅读)。
  useEffect(() => {
    if (initialDraft) inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 只在挂载时
  }, []);
  // 最近作品默认折叠(不常驻挤压对话),按需展开。出图偏好已改由对话自动收集→记忆召回注入,不再手动选。
  const [showRecent, setShowRecent] = useState(false);
  // 对话中途技能入口:空态消息区已自动浮网格,开聊后靠「技能」按钮再唤出(不再只在开场可见)。
  const [showSkills, setShowSkills] = useState(false);
  // C2 抽屉内视图:chat=当前对话 / history=可搜历史时间线(只读回看,不改持久数据)。
  const [view, setView] = useState<"chat" | "history">("chat");
  const scrollRef = useRef<HTMLDivElement>(null);

  // D2* 自动滚到底:新消息 / 开场白 / 思考态变化时把对话滚到最新(开场白随流上滚,不再钉顶)。
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, proactive.data, loading, pendingConfirm]);

  // 「历史看得见」不等于「模型还记得」:回灌完要按服务端探针如实说出来(见 contextState)。
  const [ctxNotice, setCtxNotice] = useState<ContextNotice | null>(null);
  const hydratedRef = useRef(false);

  // #42 进入回灌:PC 的会话态是 module 级内存 Map,**刷新即清零**(老板实报「刷新后原本的
  // 内容没了」;干净基线真机复现:发出去看得见 → 刷新就没了)。而转录一直在库里 ——
  // 同一时刻打 /advisor/topics/{id}/thread,那两条 user+assistant 原样躺着 ⇒ 纯前端没接回。
  // H5 早有这条(I14a),PC 从来没有。只在本地线程为空时拉一次,ref 守卫防重入。
  //
  // ★topicId 取自**只读探针**而不是 proactive:满屏页 opening="briefing" 时
  //   `useProactiveOpen(sessionId, !briefingMode)` 被禁用 ⇒ proactive.data 恒 undefined,
  //   而 **#42 报的就是满屏页**。探针本身就带 topicId,一口拿齐,两种模式同一条路。
  useEffect(() => {
    if (hydratedRef.current || messages.length > 0) return;
    hydratedRef.current = true;
    advisorChatApi
      .sessionState(sessionId)
      .then(async (st) => {
        if (!st?.topicId) return; // 没有话题 = 没有历史可接,也就不该弹任何话
        const thread = await advisorTopicsApi.topicThread(st.topicId);
        const restored = (thread ?? [])
          .filter((m) => m.content && (m.msgRole === "user" || m.msgRole === "assistant"))
          .map((m) => ({
            role: m.msgRole === "user" ? ("user" as const) : ("advisor" as const),
            text: m.content,
          }));
        if (restored.length === 0) return;
        // 1004 A1:刷新前没回答的那张确认卡一并接回(按钮回来,不再被下一句话误续接)
        hydrateMessages(restored, st.pendingConfirm);
        // 历史摆上屏了,才按探针如实说「模型那边接回来了没」。
        // ★这里**不做 alive/已卸载 gate**:回灌一落地 messages.length 就变、effect 依赖随之
        //   变化,gate 住等于这句话永远不出现,而失效方式是**安静地不弹** —— 正是要防的那种。
        setCtxNotice(contextNotice(st, restored.length));
      })
      .catch(() => {
        /* 回灌是增强路径,失败静默(空线程开局,不比现状差);探针挂了也不弹,
           免得把「我读不到状态」渲染成「模型不记得」 */
      });
  }, [sessionId, messages.length, hydrateMessages]);

  /**
   * #28/#35:从历史点开继续。
   * ★**先验切没切成,再决定要不要把那条历史摆上屏** —— 判据是
   *   `topicId === requestedTopicId`,不是"有没有报错"(实测过 200 + 回另一条的真缺陷)。
   *   没切成还把历史灌进去 = 界面显示 B、模型在 A,静默且用户无从察觉。
   */
  const onContinueTopic = async (topicId: string) => {
    try {
      const st = await advisorChatApi.continueTopic(sessionId, topicId);
      if (!switchedToRequested(st, topicId)) {
        setCtxNotice(switchFailedNotice(st, topicId));
        return; // 停在历史视图,不装作切过去了
      }
      const thread = await advisorTopicsApi.topicThread(topicId);
      const msgs = (thread ?? [])
        .filter((m) => m.content && (m.msgRole === "user" || m.msgRole === "assistant"))
        .map((m) => ({
          role: m.msgRole === "user" ? ("user" as const) : ("advisor" as const),
          text: m.content,
        }));
      replaceMessages(msgs); // 用户明确要求切 → 整体替换(与被动回灌不同)
      hydratedRef.current = true; // 已经是这条话题了,别再被"进入回灌"覆盖一次
      setView("chat");
      setCtxNotice(contextNotice(st, msgs.length));
    } catch {
      setCtxNotice({
        tone: "warn",
        text: "这段历史没能接过来,稍后再试 —— 界面还停在原来那段对话上。",
      });
    }
  };

  const onSend = () => {
    const t = draft.trim();
    // 传图进行中也禁发:import 是串行网络往返,过早 send 会捕到未就绪的附件盘 → 丢图。
    if (!t || loading || uploading) return;
    clearUploadError(); // 成功发出一条即清掉上一批传图的失败轻提示,不跨消息残留
    setDraft("");
    void send(t);
  };

  // 确认后 agent 异步编排出图/出片(落 task)。做图**不再内联渲染在对话里**(问题2 重设计:图会把
  // 输入框挤出抽屉、且对话区拥挤)——改为展开「最近作品」这条**跳转**清单,新 task 出现后点它跳作品详情页看。
  // 主动 invalidate 让进度条立即重取(否则初始空列表 nextRefetchInterval 已停轮询,新 task 不自动浮现)。
  const onConfirm = () => {
    setShowRecent(true);
    void confirm().then(() => qc.invalidateQueries({ queryKey: ["advisor", "creations"] }));
  };

  // 开新话题:清 ai 工作上下文 + 本地会话态,再 resetQueries proactive → 旧开场白立即清空、重拉空开局开场白(mint 新 topic)。
  // 记忆(偏好/画像/历史结论)仍每 turn 注入 → 干净但不失忆。用 resetQueries 而非 invalidate:后者会残留旧开场气泡直到 refetch 完成
  // (失败则永久,codex console P2)。失败(hook 已 setError)则 .catch 吞掉 rejection,不重置 proactive。
  const onNewTopic = () => {
    setShowSkills(false); // 回空态,技能面板改由空态网格接管(不残留浮层态)
    // 0901 用户实报「开了新话题还显示上一轮的」之②:messages 清零会让「进入回灌」effect
    // 重新武装(缓存非空挂载那条路 ref 未置 true),而此刻探针仍指旧话题(新 topic 要等
    // proactive open 才 mint)——放它跑,就把上一轮对话原样灌回刚开的新话题。
    // ★必须在清线程**之前**武装:effect 是「同步置 ref → 异步拉线程」,等 .then 再置已经
    //   拦不住发出去的那一枪。开新话题 = 本次挂载不再需要被动恢复(失败路留着它也无害:
    //   会话没清,messages 非空,回灌本来就不会跑)。
    hydratedRef.current = true;
    void newTopic()
      .then(() => {
        // 之①:提示条此前全仓没有 setCtxNotice(null) 的调用点,「更早的 N 轮没接回来」
        // 一旦出现就永远挂着。新话题后屏幕上已无历史,「看得见但不算数」的前提消失,必须清。
        setCtxNotice(null);
        return qc.resetQueries({ queryKey: ["advisor", "proactive", sessionId] });
      })
      .catch(() => {}); // 失败(hook 已 setError)保留当前会话:历史还在屏上,提示条也该留着
  };

  // C2:历史时间线接管整个抽屉主体(自带「返回对话」回到聊天)。
  if (view === "history") {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <ContextNoticeBar notice={ctxNotice} />
        <div className="min-h-0 flex-1">
          <TopicHistory onBack={() => setView("chat")} onContinue={onContinueTopic} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex shrink-0 items-center justify-end gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs text-muted-foreground"
          onClick={() => setView("history")}
          title="回看以前聊过的话题(可搜标题/摘要)"
        >
          <History className="h-3.5 w-3.5" aria-hidden />
          历史
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs text-muted-foreground"
          onClick={onNewTopic}
          disabled={loading}
          title={`换个话题聊,${persona.title}记得你的偏好和历史`}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          开新话题
        </Button>
      </div>
      {/* min-h-0 关键:flex-1 默认 min-height:auto 不肯收缩,消息多时会把下方的输入框顶出 overflow-hidden
          抽屉(问题2「点最近作品输入框不见了」根因)。min-h-0 让消息区可收缩滚动,输入框恒在底部可见。 */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto rounded-md border p-4">
        {/* ADV-UI-2 满屏页开场三块(真招呼+任务卡+chips);开聊后随消息流上滚,不钉顶。
            骨架屏全程占位 —— 老板头号靶「开屏是白的体感不好」。chips 填话进输入框,不发送。 */}
        {briefingMode && messages.length === 0 && (
          <AdvisorBriefing
            briefing={briefing.data ?? null}
            loading={briefing.isLoading}
            failed={briefing.isError}
            onChipPick={(text) => {
              setDraft(text);
              inputRef.current?.focus();
            }}
          />
        )}
        {/* C1(2026-08-17):空态不再铺整块技能网格 —— 2.0 自动路由后「先挑技能」的前提消失。
            对话区留一句引导,示例 chips 挪到输入框上方(见下),完整网格进「更多能力」抽屉。 */}
        {!briefingMode &&
          messages.length === 0 &&
          !proactive.data?.text &&
          !proactive.isLoading && (
            <div className="text-sm text-muted-foreground">
              直接说一句话,比如「把这条爆款仿拍成我们的版本」—— 我来判断该调哪个能力舱。
            </div>
          )}
        {!briefingMode && proactive.isLoading && (
          <div className="text-sm text-muted-foreground">{persona.title}正在看今天的盘…</div>
        )}
        {!briefingMode && proactive.data?.text ? (
          <Bubble
            who="advisor"
            text={proactive.data.text}
            evidence={proactive.data.evidence}
            aiLabel={showAiLabel}
          />
        ) : null}
        {messages.map((m, i) => {
          // 1004 F23b:卡还挂着时,最后一条军师正文里的「卡面复述」摘掉(卡上已经摆着同一段)
          const text =
            pendingConfirm && m.role === "advisor" && i === messages.length - 1
              ? stripConfirmEcho(m.text, pendingConfirm)
              : m.text;
          if (!text && !m.styleGallery && !m.handoffs) return null;
          return (
            <Bubble
              key={i}
              who={m.role}
              aiLabel={showAiLabel}
              text={text}
              evidence={m.evidence}
              styleGallery={m.styleGallery}
              handoffs={m.handoffs}
              onPickStyle={(item) => {
                if (loading) return; // 在途 turn 不叠发(与输入框禁用同一语义)
                void send(`就用「${item.label}」风格`);
              }}
            />
          );
        })}
        <ContextNoticeBar notice={ctxNotice} />
        {/* 整轮返回、不流式:这段等待此前只有一行静态小字,和卡死同形(见 ThinkingIndicator 头注) */}
        {loading && <ThinkingIndicator />}
        {/* 报错要「说得出是什么错」(bug 3:界面只剩「请求失败」四个字,既看不出哪步挂了,
            也没法拿去捞日志)。错误码/traceId 单独一行小字,用户能整段复制给我们。 */}
        {error && (
          <div
            className="mt-2 rounded-md bg-destructive/10 px-2.5 py-2"
            data-testid="advisor-error"
          >
            <div className="text-sm text-destructive">{error.text}</div>
            {(error.errorCode || error.traceId) && (
              <div className="mt-0.5 select-all font-mono text-2xs text-destructive/70">
                {advisorDiagnosticLine(error)}
              </div>
            )}
          </div>
        )}
        {/* ②(2026-08-17,老板真机:出片后弹框把跟军师的对话挤得看不见):确认卡/风格画廊放进
            对话滚动流里,随消息一起滚 —— 不再做对话区外的固定块把对话上下文顶出视口。input 恒在底部。 */}
        {pendingConfirm && (
          <Card className="mt-3 max-w-[38rem]" data-testid="advisor-confirm">
            <CardContent className="space-y-3 p-4">
              {/* ★确认卡是**花钱的闸门**,它自己不许崩。真浏览器实测:少一个 quote,
                  `quote.summary` 直读就 `Cannot read properties of undefined` ——
                  **整个对话面板消失**(输入框都没了),而后端那一单还挂着,
                  用户既确认不了也取消不了,看到的正是「确认卡没有出来」。
                  ai 侧 quote 来自 `await tool.quote(...)`,它自己有三处按
                  `(pending.get("quote") or {})` 当可缺处理 —— 前端不该比它更脆。
                  缺了就退化显示(退到工具名),**一个字段坏掉没有资格否决整份产物**。 */}
              {/* ADV-UI-CONFIRM-1(2026-08-26):规格段限高 + 独立滚动。
                  ★手机端早就有这两道(`.av-gate-s` 的 max-height + overflow-y),**PC 侧一直没有** ——
                    同一个缺陷修了一半。真实报价(尤其详情长图那 10 种)规格很长,
                    没有限高就会把整屏吃满,用户看不到自己刚说的那句是对着哪一单确认的。
                  ★overscroll-contain:滚到底不把外层消息区一起带动。 */}
              <div
                className="max-h-40 overflow-y-auto overscroll-contain text-sm leading-relaxed"
                data-testid="advisor-confirm-spec"
              >
                {pendingConfirm.quote
                  ? splitQuoteSummary(pendingConfirm.quote.summary).spec
                  : `确认执行:${pendingConfirm.tool ?? "这一步"}`}
              </div>
              {/* C2:用户没定风格 → 报价里带风格条,点卡确定性 patch 后重报价(不经 LLM);风格定了本字段消失。 */}
              {pendingConfirm.quote?.styleGallery && (
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground">
                    顺手挑个风格(点卡即用),或直接确认按默认出:
                  </div>
                  <StyleGalleryRow
                    styles={pendingConfirm.quote.styleGallery!}
                    onPick={(item) => {
                      if (loading) return;
                      void pickStyle(item.key);
                    }}
                  />
                </div>
              )}
              {/* ★「本次不会出现」独立成块:原来它是 summary 末尾一句,埋在三行密排的散文里。
                  这是**用户花钱前唯一能自己接住的信息**,不该跟规格挤在同一段。 */}
              {pendingConfirm.quote &&
                (() => {
                  const missing = splitQuoteSummary(pendingConfirm.quote.summary).missing;
                  return missing ? (
                    <div
                      className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2"
                      data-testid="advisor-confirm-missing"
                    >
                      <div className="text-xs font-semibold text-warning">本次不会出现</div>
                      <div className="mt-1 text-sm leading-relaxed">
                        {missing}
                        <span className="text-muted-foreground"> —— 要写就现在说一句。</span>
                      </div>
                    </div>
                  ) : null;
                })()}
              {/* ★报价提到卡面最大的字:改之前它和旁边那段说明**同号**(都 14px),
                  而它是老板唯一必须看清的数。文案仍走 priceText 原样抄,不在这里算。 */}
              <div className="text-lg font-semibold text-primary">
                {/* ADV-COST-TONE-1(老板令):估算为 0 的动作不渲染钱字样——免费不当卖点;
                  真扣积分的照常报价。确认门机制本身不动。 */}
                {/* 报价缺席时**不编价格**:说不出就只问"做吗",绝不显示成 0 积分 ——
                    「不知道多少钱」和「不要钱」是两件事,渲染成同一句话就又同形了。 */}
                {/* ★0926:est_credits 为 null/缺省(价表取不到)→「价格待系统核算」,不显数字也不显免费;
                    === 0 维持 ADV-COST-TONE-1 只问「确认做吗?」。分类唯一判据在 classifyQuote。 */}
                {pendingConfirm.quote && classifyQuote(pendingConfirm.quote).kind !== "free"
                  ? `${priceText(pendingConfirm.quote)},确认做吗?`
                  : "确认做吗?"}
              </div>
              <div className="flex gap-2">
                <Button size="sm" disabled={loading} onClick={onConfirm}>
                  确认做
                </Button>
                <Button size="sm" variant="outline" disabled={loading} onClick={() => cancel()}>
                  先不做
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* 「最近作品」= 做图移出对话后的跳转清单;限高可滚,shrink-0 也不会把输入框顶出抽屉。 */}
      {showRecent && (
        <div className="max-h-[32vh] shrink-0 overflow-y-auto">
          <RecentCreationsStrip />
        </div>
      )}

      {/* C1「更多能力」抽屉:完整技能网格从常驻改为按需展开(空态/开聊都可开);点卡即用并收起。 */}
      {showSkills && (
        <div className="max-h-[40vh] shrink-0 overflow-y-auto rounded-md border p-2">
          <SkillGrid
            onPick={(label) => {
              setShowSkills(false);
              void send(label);
            }}
            scene={scene}
          />
        </div>
      )}

      {/* C1 示例 chips:空态放输入框上方(点=喂话走自动路由);开聊后交给 SceneSkillBar「走到哪推哪」。
          ADV-UI-2:briefing 模式空态的建议 chips 已在开屏三块里,这排收掉免双排冗余。 */}
      {messages.length === 0 ? (
        briefingMode ? null : (
          <SkillChips scene={scene} onPick={(label) => void send(label)} />
        )
      ) : (
        <SceneSkillBar scene={scene} onPick={(label) => void send(label)} />
      )}

      {/* 弱化的收纳条:默认折叠,点开才显「更多能力」网格 / 最近作品,腾出对话与发送框空间 */}
      <div className="flex items-center gap-1.5">
        <Button
          type="button"
          variant={showSkills ? "secondary" : "ghost"}
          size="sm"
          className="h-7 gap-1 px-2 text-xs text-muted-foreground"
          aria-pressed={showSkills}
          onClick={() => setShowSkills((v) => !v)}
        >
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          更多能力
        </Button>
        {/* 调用方已把「最近作品」常驻在别处时不重复(满屏页右栏)。见 hideRecentShortcut 头注。 */}
        {!hideRecentShortcut && (
          <Button
            type="button"
            variant={showRecent ? "secondary" : "ghost"}
            size="sm"
            className="h-7 gap-1 px-2 text-xs text-muted-foreground"
            aria-pressed={showRecent}
            onClick={() => setShowRecent((v) => !v)}
          >
            <Clock className="h-3.5 w-3.5" aria-hidden />
            最近作品
          </Button>
        )}
      </div>

      {/* 传图失败轻提示:单张失败不阻断其余,失败几张在此说清(不是只弹一句「失败」) */}
      {uploadError && (
        <div className="mb-2 text-xs text-destructive" role="status">
          {uploadError}
        </div>
      )}

      {/* 附件盘:本次待发消息附的上传图(可删) */}
      {pendingUploads.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2" aria-label="待发附图">
          {pendingUploads.map((u) => (
            <div key={u.assetId} className="relative">
              <img
                src={u.url}
                alt="附图"
                className="h-12 w-12 rounded border border-border object-cover"
              />
              <button
                type="button"
                aria-label="移除附图"
                className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-muted text-2xs leading-none text-muted-foreground shadow"
                onClick={() => removeUpload(u.assetId)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        {/* 真 input 隐藏,用户看见的是下面那个 icon-only 按钮(所以它必须带 aria-label) */}
        <input
          ref={fileInputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => void pickImages(e.target.files)}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="传图"
          aria-busy={uploading}
          disabled={loading || uploading}
          onClick={openPicker}
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <ImagePlus className="h-4 w-4" aria-hidden />
          )}
        </Button>
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSend()}
          placeholder={`问${persona.title} / 让${persona.title}动手…`}
          disabled={loading || uploading}
        />
        <Button onClick={onSend} disabled={loading || uploading || !draft.trim()}>
          发送
        </Button>
      </div>
    </div>
  );
}
